import React, { useState, SetStateAction, useEffect } from 'react';
import {
  Grid,
  Typography,
  Box,
  InputLabel,
  Select,
  MenuItem,
  Badge,
  CircularProgress,
  TextField,
  Button,
} from '@material-ui/core';
import { LeafletMouseEvent } from 'leaflet';
import { differenceInMinutes } from 'date-fns';
import DateFnsUtils from '@date-io/date-fns';
import { ptBR } from 'date-fns/locale';
import { MuiPickersUtilsProvider, KeyboardDateTimePicker } from '@material-ui/pickers';
import MapMarker from '../MapMarker';
import { useStyles } from './styles';
import api, { googleApi } from '../../services/api';
import { missionType, missionFocus } from '../../pages/AddSurveillance/utils';
import {
  Coordinates,
  SurveillanceMission as SurveilanceMissionType,
  PeriodType,
  ViaDetailsObject,
} from '../../@types/surveilanceTypes';
import SubInputWarn from '../SubInputWarn';
import ShowSnackbar, { SetSnackbarType } from '../SnackBar';
import {
  GoogleMapsReverseResponseType,
  GoogleMapsSearchResponseType,
} from '../../@types/geocodingTypes';

interface IProps {
  surveilanceMission: SurveilanceMissionType;
  setSurveilanceMission: React.Dispatch<SetStateAction<SurveilanceMissionType>>;
  latlng: Coordinates;
  setLatLng: React.Dispatch<SetStateAction<Coordinates>>;
  period: PeriodType;
  setPeriod: React.Dispatch<SetStateAction<PeriodType>>;
  formErrors: { key: string; message: string }[];
  setFormErrors: React.Dispatch<SetStateAction<{ key: string; message: string }[]>>;
  locationText: string;
  setLocationText: React.Dispatch<SetStateAction<string>>;
}

const SurveilanceTopForm: React.FC<IProps> = ({
  surveilanceMission,
  setSurveilanceMission,
  latlng,
  setLatLng,
  period,
  setPeriod,
  formErrors,
  setFormErrors,
  locationText,
  setLocationText,

}) => {
  const [surveilanceState, setSurveilanceState] = useState<SurveilanceMissionType>(
    surveilanceMission,
  );
  const [periodState, setPeriodState] = useState<PeriodType>(period);
  const [snackBar, setSnackbar] = useState<SetSnackbarType>({ open: false, message: '' });
  const [searchText, setSearchText] = useState<string>('');
  const [geoLoading, setGeoLoading] = useState<boolean>(false);
  const [errors, setErrors] = useState<{ key: string; message: string }[]>([]);
  const classes = useStyles();
  const geocodeKey = `${process.env.REACT_APP_GMAPS_API_KEY}`;

  const getAddress = async (event: LeafletMouseEvent) => {
    setGeoLoading(true);
    setLatLng({ lat: event.latlng.lat, lng: event.latlng.lng });
    setSearchText('');
    try {
      const { data } = await googleApi.get<GoogleMapsReverseResponseType>(
        `/api/geocode/json?latlng=${event.latlng.lat},${event.latlng.lng}&key=${geocodeKey}`,
      );

      const address = data.results[0];
      setLocationText(
        `${
          address.address_components[1].long_name ||
          address.address_components[0].long_name
        } ${`${address.address_components[0].short_name} - ` || ''}${
          address.address_components[2].short_name || ''
        }`,
      );
      setGeoLoading(false);
    } catch {
      setGeoLoading(false);
      setLocationText('');
      setSnackbar({
        open: true,
        message: 'Não foi posível realizar a geolocalização. Tente novamente.',
      });
    }
  };

  const getGeolocation = async () => {
    setGeoLoading(true);
    try {
      let treatString = searchText.replace(/[\u0020-\u002F]/g, ' ');
      treatString = treatString.replace(/\s/g, ' ');
      const { data } = await googleApi.get<GoogleMapsSearchResponseType>(
        `/api/geocode/json?address=${treatString}&language=pt-BR&key=${geocodeKey}`,
      );

      if (data.status !== 'OK') {
        setGeoLoading(false);
        setSnackbar({
          open: true,
          message: 'Não foi possível encontrar o endereço informado.',
        });
        return;
      }
      const address = data.results[0];

      setLocationText(
        `${
          address.address_components[1].long_name ||
          address.address_components[0].long_name
        } ${`${address.address_components[0].short_name} - ` || ''}${
          address.address_components[2].short_name || ''
        }`,
      );

      setLatLng({
        lat: address.geometry.location.lat,
        lng: address.geometry.location.lng,
      });

      setGeoLoading(false);
    } catch {
      setGeoLoading(false);
      setSnackbar({
        open: true,
        message: 'Não foi possível encontrar o endereço informado.',
      });
    }
  };

  const handleChange = (key: string, value: string) => {
    setSurveilanceState({ ...surveilanceMission, [key]: value });
  };

  const handleCoordinate = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name } = event.target;
    const value = Number(event.target.value);
    setLatLng({ ...latlng, [name]: value });
  };

  const validateDate = () => {
    const { occuredFrom, occuredTo } = periodState;

    const diffMin = differenceInMinutes(new Date(occuredTo), new Date(occuredFrom));
    const diffHour = diffMin / 60;

    if (diffMin < 1) {
      const list = formErrors.filter((item) => {
        return item.key === 'missionDate';
      });

      if (!list.length) {
        setFormErrors((prev) => {
          const l = [...prev];

          l.push({ key: 'missionDate', message: 'A missão não pode durar 0 horas.' });
          return l;
        });
        return;
      }
    } else if (diffHour > 23) {
      const list = formErrors.filter((item) => {
        return item.key === 'missionDate';
      });

      if (!list.length)
        setFormErrors((prev) => {
          const l = [...prev];

          l.push({
            key: 'missionDate',
            message: 'A missão de fiscalização não pode durar mais que 23 horas.',
          });
          return l;
        });
      return;
    }
    setFormErrors((prev) => {
      return prev.filter((item) => item.key !== 'missionDate');
    });
  };

  const handlePeriodChange = (key: string, date: Date | null) => {
    if (date?.getTime()) {
      if (key === 'startDate') {
        setPeriodState((prev) => {
          return { ...prev, occuredFrom: date.toISOString() };
        });
      } else {
        setPeriodState((prev) => {
          return { ...prev, occuredTo: date.toISOString() };
        });
      }
    }
  };

  useEffect(() => {
    setSurveilanceState(surveilanceMission);

    setPeriodState({ occuredFrom: period.occuredFrom, occuredTo: period.occuredTo });
    const mapErrors = formErrors.filter((item) => {
      return item.key === 'missionLocation';
    });
    setErrors(mapErrors);
  }, [formErrors, period, surveilanceMission]);

  return (
    <Box className={classes.sectionContainer}>
      <Typography variant="h6">Tipo da Missão</Typography>
      <Grid container xl={12} spacing={2}>
        <Grid item md={6} sm={12} xs={12}>
          <InputLabel className={classes.label}>Tipo da Missão</InputLabel>

          <Select
            variant="outlined"
            className={classes.select}
            value={surveilanceState['Tipo da Missão']}
            onChange={(event) => {
              handleChange('Tipo da Missão', event.target.value as string);
            }}
            onBlur={() => setSurveilanceMission(surveilanceState)}
          >
            {missionType.map((item) => (
              <MenuItem value={item} key={item}>
                {item}
              </MenuItem>
            ))}
          </Select>
        </Grid>
        <Grid item md={6} sm={12} xs={12}>
          <InputLabel className={classes.label} htmlFor="search">
            Descrição do Tipo da Missão
          </InputLabel>
          <input
            name="Descrição do Tipo da Missão"
            type="text"
            className={classes.input}
            value={surveilanceState['Descrição do tipo da Missão']}
            onChange={(event) => {
              handleChange('Descrição do tipo da Missão', event.target.value);
            }}
            onBlur={() => setSurveilanceMission(surveilanceState)}
          />
        </Grid>

        <Grid item md={6} sm={12} xs={12}>
          <InputLabel className={classes.label} htmlFor="Foco da Missão">
            Foco da Missão
          </InputLabel>

          <Select
            name="Foco da Missão"
            variant="outlined"
            className={classes.select}
            value={surveilanceState['Foco da Missão']}
            onChange={(event) => {
              handleChange('Foco da Missão', event.target.value as string);
            }}
            onBlur={() => setSurveilanceMission(surveilanceState)}
          >
            {missionFocus.map((item) => (
              <MenuItem value={item} key={item}>
                {item}
              </MenuItem>
            ))}
          </Select>
        </Grid>

        <Grid item md={6} sm={12} xs={12}>
          <InputLabel className={classes.label} htmlFor="search">
            Descrição do Foco da Missão
          </InputLabel>
          <input
            name="Descrição do Foco da Missão"
            type="text"
            className={classes.input}
            value={surveilanceState['Descrição do Foco da Missão']}
            onChange={(event) => {
              handleChange('Descrição do Foco da Missão', event.target.value);
            }}
            onBlur={() => setSurveilanceMission(surveilanceState)}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2} xs={12} className={classes.searchBar}>
        <Grid item sm={12} md={10} xs={12}>
          <InputLabel className={classes.label}>Pesquisar endereço</InputLabel>
          <TextField
            name="Pesquisar"
            placeholder="Pesquisar endereço"
            value={searchText}
            onChange={(event) => setSearchText(event.target.value)}
            variant="outlined"
            size="small"
            className={classes.textField}
          />
        </Grid>
        <Grid item sm={2} xs={2}>
          <Button
            className={classes.buttonSearch}
            variant="contained"
            color="primary"
            onClick={getGeolocation}
            disabled={!searchText}
          >
            Pesquisar
          </Button>
        </Grid>
      </Grid>
      <Grid container md={12} sm={12} xs={12}>
        <InputLabel className={classes.label}>Localização</InputLabel>
        <Badge
          color="secondary"
          badgeContent={errors.length}
          invisible={!errors.length}
          style={{
            margin: '0 0 5px 0',
            width: '100%',
            height: '100%',
            backgroundColor: 'white',
            border: `${!errors.length ? '1px solid transparent' : '1px solid red'}`,
          }}
        >
          <div className={classes.mapContainer}>
            <MapMarker
              onClick={getAddress}
              point={[latlng.lat, latlng.lng]}
              notDragabble={false}
            />
            <div className={classes.mapFeedback}>
              {geoLoading ? (
                <CircularProgress size={23} thickness={5} />
              ) : (
                <Typography variant="subtitle2" style={{ fontWeight: 'normal' }}>
                  {locationText}
                </Typography>
              )}
            </div>
          </div>
        </Badge>
        <SubInputWarn
          type="error"
          formWarns={formErrors.filter((item) => item.key === 'missionLocation')}
        />
      </Grid>

      <Grid container xl={12} spacing={3}>
        <Grid item sm={12} md={12} xs={12}>
          <InputLabel className={classes.label} htmlFor="search">
              Endereço
          </InputLabel>
          <input 
              autoComplete="off"
              name="data.driverIncidenteDetails"
              type="text"
              placeholder="Informe o endereço"
              onChange={(event) => {
                setLocationText(event.target.value);
              }}
              value={locationText}
              className={`${classes.input}`}
          />
        </Grid>
        <Grid item sm={6} md={6} xs={12}>
          <InputLabel className={classes.label} htmlFor="search">
            Latitude
          </InputLabel>
          <input
            name="lat"
            type="number"
            className={classes.input}
            value={latlng.lat}
            onChange={handleCoordinate}
          />
        </Grid>
        <Grid item sm={6} md={6} xs={12}>
          <InputLabel className={classes.label} htmlFor="search">
            Longitude
          </InputLabel>
          <input
            name="lng"
            type="number"
            className={classes.input}
            value={latlng.lng}
            onChange={handleCoordinate}
          />
        </Grid>

        <Grid item sm={6} xs={12}>
          <InputLabel
            className={classes.label}
            htmlFor="search"
            error={!!formErrors.filter((item) => item.key === 'missionDate').length}
          >
            Data de Início
          </InputLabel>
          <MuiPickersUtilsProvider utils={DateFnsUtils} locale={ptBR}>
            <KeyboardDateTimePicker
              variant="inline"
              format="dd/MM/yyyy HH:mm"
              placeholder="dd/mm/yyyy hh:mm"
              ampm={false}
              className={classes.dateInput}
              value={periodState.occuredFrom}
              onChange={(event) => handlePeriodChange('startDate', event)}
              onClose={() => {
                setPeriod(periodState);
                validateDate();
              }}
              onBlur={() => {
                setPeriod(periodState);
                validateDate();
              }}
              inputVariant="outlined"
              inputProps={{ style: { padding: '0 0 0 15px', height: 40 } }}
              error={!!formErrors.filter((item) => item.key === 'missionDate').length}
              showTodayButton
              helperText=""
            />
            <SubInputWarn
              type="error"
              formWarns={formErrors.filter((item) => item.key === 'missionDate')}
            />
          </MuiPickersUtilsProvider>
        </Grid>

        <Grid item sm={6} xs={12}>
          <InputLabel
            className={classes.label}
            htmlFor="search"
            error={!!formErrors.filter((item) => item.key === 'missionDate').length}
          >
            Data e Hora de Fim
          </InputLabel>
          <MuiPickersUtilsProvider utils={DateFnsUtils} locale={ptBR}>
            <KeyboardDateTimePicker
              variant="inline"
              format="dd/MM/yyyy HH:mm"
              placeholder="dd/mm/yyyy hh:mm"
              ampm={false}
              className={classes.dateInput}
              value={periodState.occuredTo}
              onChange={(event) => handlePeriodChange('endDate', event)}
              onClose={() => {
                setPeriod(periodState);
                validateDate();
              }}
              onBlur={() => {
                setPeriod(periodState);
                validateDate();
              }}
              inputVariant="outlined"
              inputProps={{ style: { padding: '0 0 0 15px', height: 40 } }}
              error={!!formErrors.filter((item) => item.key === 'missionDate').length}
              showTodayButton
              helperText=""
            />
            <SubInputWarn
              type="error"
              formWarns={formErrors.filter((item) => item.key === 'missionDate')}
            />
          </MuiPickersUtilsProvider>
        </Grid>
      </Grid>
      <ShowSnackbar
        open={snackBar.open}
        message={snackBar.message}
        setSnackbar={setSnackbar}
      />
    </Box>
  );
};

export default SurveilanceTopForm;
